// Description: Component that shows an analysis of customer sentiments
// Updated: 20/08/2025
// Author: Vitor de Jesus

// React
import React, { useEffect, useState } from 'react';

// Material UI Components
import { Box } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';

// Components and Functions
import StatusIcon from '../../components/StatusLabel/StatusIcon';
import Titles from '../../components/Titles/Titles';
import ThemeColors from '../../components/ThemeColors/ThemeColors';
import { ReactTyped } from 'react-typed';
import { SentimentsAnalysis } from '../../api/n8n/ai/SentimentsAnalysis';

// Render The Component
const Comprehend = ({ sentiments, totalMessages, loading }) => {

    const defaultSentiments = {
        Positive: 0,
        Negative: 0,
        Neutral: 0,
        Mixed: 0
    };

    const mergedSentiments = { ...defaultSentiments, ...sentiments };

    const colorMappingSentiments = {
        'Positive': {
            iconColor: '#4C8435',
            backgroundColor: '#D3EAC8',
            textColor: '#345427',
        },
        'Negative': {
            iconColor: '#CD4747',
            backgroundColor: ThemeColors.red01,
            textColor: '#4E1B1D',
        },
        'Neutral': {
            iconColor: '#8142FC',
            backgroundColor: '#DDD4FF',
            textColor: '#4F189A',
        },
        'Mixed': {
            iconColor: '#848484',
            backgroundColor: '#E5E5E5',
            textColor: '#303035',
        }
    };

    const hexToRgb = (hex) => {
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            hex = hex.split('').map(x => x + x).join('');
        }
        const num = parseInt(hex, 16);
        return `${(num >> 16) & 255}, ${(num >> 8) & 255}, ${num & 255}`;
    };

    const [loadingAi, setLoadingAi] = useState(false);
    const [typeView, setTypeView] = useState('analysis');
    const [typedText, setTypedText] = useState(''); // Novo estado para o texto com efeito de digitação

    const generateAiAnalysis = async () => {

        try {

            setLoadingAi(true);  // Inicia o carregamento
            setTypedText(''); // Limpa o texto digitado anteriormente

            const data = {
                sentiments: mergedSentiments,
                totalMessages: totalMessages,
            };

            setTypeView('analysis_ai');

            const response = await SentimentsAnalysis(data); // Chama a função de análise de sentimentos

            if (response && response.data && response.data.length > 0) {

                const newMessage = response.data[0].message;

                setTypedText(newMessage); // Atualiza o texto com efeito de digitação
                console.log("Resposta da IA:", newMessage);

            } else {
                setTypedText('Não há dados suficientes para gerar uma análise com IA. Tente novamente quando houver mais mensagens. Caso ache que isso é um erro, entre em contato com o suporte.');
            }

        } catch (error) {
            console.error("Erro ao melhorar o texto:", error);
        }
        finally {
            setLoadingAi(false); // Finaliza o carregamento
        }
    };

    const [dots, setDots] = useState('');

    useEffect(() => {
        const interval = setInterval(() => {
            setDots(prev => {
                if (prev.length === 3) return '';
                return prev + '.';
            });
        }, 500); // Altera os pontos a cada 500ms

        // Limpeza do intervalo quando o componente for desmontado
        return () => clearInterval(interval);
    }, []);


    return (
        <>
            <Box
                flex={1}
                borderRadius={'12px'}
                border={'1px solid #D9D9DC'}
                padding={'30px'}
                sx={{
                    background: '#fff',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: { xs: '220px', xl: '312px', sm: '290px' }
                }}
            >
                <Typography sx={{ color: '#8033F4', fontWeight: 'bold', fontSize: { xl: '30px', xs: '20px' }, marginBottom: '10px' }}>
                    Análise Geral de Sentimentos <Typography component="span" style={{ fontWeight: 'normal', fontSize: '20px' }}> ( Clientes )</Typography>
                </Typography>
                <Box display="flex" flexDirection="column" width="100%" height="90%" alignItems="lefts" sx={{ overflowY: 'hidden', paddingRight: '10px', maxHeight: '200px' }}>

                    {!loading && mergedSentiments && typeView === 'analysis' && Object.keys(mergedSentiments).map((key, index) => (
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            padding="0 12px"
                            key={index}
                            sx={{
                                background: colorMappingSentiments[key].backgroundColor,
                                height: '32px',
                                borderRadius: '6px',
                                width: `100%`,
                                marginBottom: '8px',
                                minWidth: '100%',
                            }}
                        >
                            <Box display="flex" gap="7px" alignItems="center" flex="1">
                                <StatusIcon status="Alert" color={colorMappingSentiments[key].iconColor} width="12px" height="12px" />
                                <Titles children={key === 'Mixed' ? 'Sentimentos mistos' : key === 'Neutral' ? 'Sentimentos neutros' : key === 'Positive' ? 'Sentimentos positivos' : 'Sentimentos negativos'} color={colorMappingSentiments[key].textColor} type="P2" />
                                <Titles children={`: ${mergedSentiments[key].toFixed(2)}%`} color={colorMappingSentiments[key].textColor} type="P2" />
                            </Box>
                        </Box>
                    ))}

                    {
                        !loadingAi && mergedSentiments && typeView === 'analysis_ai' && (
                            <Typography
                                variant="body1"
                                color="textSecondary"
                                sx={{
                                    fontWeight: 'normal',
                                    fontSize: '16px',
                                    whiteSpace: 'pre-line',
                                    wordWrap: 'break-word',
                                    lineHeight: '1.5',
                                }}
                            >
                                <ReactTyped
                                    strings={[typedText]}
                                    typeSpeed={10}
                                    backSpeed={25}
                                    backDelay={1000}
                                    startDelay={500}
                                    showCursor={false}
                                />
                            </Typography>
                        )
                    }

                    {
                        loadingAi && typeView === 'analysis_ai' && (
                            <Box display="flex" alignItems="center" justifyContent="left">
                                <AutoAwesomeIcon sx={{ color: ThemeColors.purple600, fontSize: '20px', marginRight: '10px' }} />
                                <Typography variant="body1">
                                    Aguarde, estou gerando a análise{dots}
                                </Typography>
                            </Box>
                        )
                    }

                    {loading &&
                        Object.keys(colorMappingSentiments).map((key, index) => (
                            <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                padding="0 12px"
                                key={index}
                                sx={{
                                    background: colorMappingSentiments[key].backgroundColor,
                                    height: '32px',
                                    borderRadius: '6px',
                                    width: `100%`,
                                    marginBottom: '8px',
                                    minWidth: '100%',
                                }}
                            >
                                <LinearProgress
                                    sx={{
                                        height: '32px',
                                        width: '100%',
                                        borderRadius: '6px',
                                        minWidth: '200px',
                                        backgroundColor: `rgba(${hexToRgb(colorMappingSentiments[key].backgroundColor)}, 0.95)`,
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: colorMappingSentiments[key].backgroundColor,
                                        }
                                    }}
                                />
                            </Box>
                        ))
                    }
                </Box>
                <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" padding={'0 10px'}>
                    {
                        loading ?
                            <LinearProgress
                                sx={{
                                    height: '36px',
                                    borderRadius: '6px',
                                    minWidth: '150px',
                                    backgroundColor: `rgba(${hexToRgb(ThemeColors.gray20)}, 0.95)`,
                                    '& .MuiLinearProgress-bar': {
                                        backgroundColor: ThemeColors.gray20
                                    },
                                }}
                            />
                            :
                            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                                {
                                    typeView === 'analysis' ?
                                        <Titles children={`${totalMessages} mensagens analisadas`} color={ThemeColors.gray60} type="P2" />
                                        : <span></span>
                                }
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<AutoAwesomeIcon sx={{ color: '#fff' }} />}
                                    onClick={typeView === 'analysis' ? generateAiAnalysis : () => setTypeView('analysis')}
                                    sx={{
                                        marginLeft: '10px',
                                        padding: { xs: '6px 14px', xl: '10px 18px' },
                                        display: loadingAi ? 'none' : 'flex',
                                        fontSize: { xs: '11px', xl: '13px' },
                                    }}
                                >
                                    {typeView === 'analysis' && loadingAi ? 'Gerando análise...' : typeView === 'analysis' ? 'Análise com IA' : 'Voltar aos dados'}
                                </Button>
                            </Box>
                    }
                </Box>
            </Box>
        </>
    );
};

export default Comprehend;
